import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  CalendarioAmadeus,
  CalendariosCampanha,
  DeParaIntegracao,
  PerfilAmadeus,
  PerfilPlanoParticipante,
  PlanoCalendario,
} from '../models/de-para-integracao.model';
import {
  CalendarioFssDto,
  CampanhaPlanoIntegracaoResponseDto,
  PerfilAmadeusResponseDto,
  PerfilPlanoParticipanteResponseDto,
  PlanoCalendarioResponseDto,
} from '../api/models';
import { extractIsoDate, formatDateIsoToBr } from '../utils/date-format.util';
import { toLocalIsoDate } from '../validators/campanha-vigencia.validator';
import { DeParaIntegracaoApiService } from './de-para-integracao-api.service';

@Injectable({
  providedIn: 'root',
})
export class DeParaIntegracaoService {
  constructor(private readonly deParaApiService: DeParaIntegracaoApiService) {}

  listar(idCampanha: number): Observable<DeParaIntegracao[]> {
    return this.deParaApiService
      .listPlanosIntegracao(idCampanha)
      .pipe(map((dtos) => dtos.map((dto) => this.mapResponseToModel(dto))));
  }

  listarParesPerfilPlano(idCampanha: number): Observable<PerfilPlanoParticipante[]> {
    return this.deParaApiService
      .listPerfisPlanosParticipantes(idCampanha)
      .pipe(map((dtos) => dtos.map((dto) => this.mapParResponseToModel(dto))));
  }

  listarCodigosAmadeus(numPlbnf: number): Observable<PerfilAmadeus[]> {
    return this.deParaApiService
      .listPerfisAmadeus(numPlbnf)
      .pipe(map((dtos) => dtos.map((dto) => this.mapAmadeusResponseToModel(dto, numPlbnf))));
  }

  criar(
    idCampanha: number,
    idPerfilInvest: number,
    numPlbnf: number,
    codPrfinv: number,
  ): Observable<DeParaIntegracao> {
    return this.deParaApiService
      .createPlanoIntegracao(idCampanha, { idPerfilInvest, numPlbnf, codPrfinv })
      .pipe(map((dto) => this.mapResponseToModel(dto)));
  }

  atualizar(
    idCampanha: number,
    idPerfilInvest: number,
    numPlbnf: number,
    codPrfinv: number,
  ): Observable<DeParaIntegracao> {
    return this.deParaApiService
      .updatePlanoIntegracao(idCampanha, idPerfilInvest, numPlbnf, { codPrfinv })
      .pipe(map((dto) => this.mapResponseToModel(dto)));
  }

  listarCalendarios(idCampanha: number): Observable<CalendariosCampanha> {
    return this.deParaApiService.listCalendarios(idCampanha).pipe(
      map((dto) => ({
        planos: dto.planos.map((plano) => this.mapPlanoCalendarioToModel(plano)),
        calendarios: dto.calendarios.map((calendario) => this.mapCalendarioToModel(calendario)),
      })),
    );
  }

  salvarCalendario(
    idCampanha: number,
    numPlbnf: number,
    codCltrprf: number | null,
  ): Observable<PlanoCalendario> {
    return this.deParaApiService
      .salvarCalendario(idCampanha, { numPlbnf, codCltrprf })
      .pipe(map((dto) => this.mapPlanoCalendarioToModel(dto)));
  }

  excluir(idCampanha: number, idPerfilInvest: number, numPlbnf: number): Observable<void> {
    return this.deParaApiService
      .deletePlanoIntegracao(idCampanha, idPerfilInvest, numPlbnf)
      .pipe(map(() => undefined));
  }

  private mapResponseToModel(dto: CampanhaPlanoIntegracaoResponseDto): DeParaIntegracao {
    return {
      idPerfilInvest: dto.idPerfilInvest,
      nomePerfil: dto.nomePerfilInvestimento?.trim() || `Perfil ${dto.idPerfilInvest}`,
      numPlbnf: dto.numPlbnf,
      nomePlano: dto.dcrPlbnf?.trim() || `Plano ${dto.numPlbnf}`,
      codPrfinv: dto.codPrfinv,
      nomeCodigoAmadeus: dto.dcrPrfinv?.trim() || `Código ${dto.codPrfinv}`,
      codUmAmadeus: dto.codUmAmadeus ?? null,
      dtInclusao: dto.dtInclusao,
      dtUltAtu: dto.dtUltAtu,
    };
  }

  private mapPlanoCalendarioToModel(dto: PlanoCalendarioResponseDto): PlanoCalendario {
    return {
      numPlbnf: dto.numPlbnf,
      nomePlano: dto.dcrPlbnf?.trim() || `Plano ${dto.numPlbnf}`,
      codCltrprf: dto.codCltrprf ?? null,
    };
  }

  private mapCalendarioToModel(dto: CalendarioFssDto): CalendarioAmadeus {
    const fim = extractIsoDate(dto.datFimVigSolCltrprf);
    // Comparação textual de ISO (`yyyy-MM-dd`), pelo mesmo motivo de extractIsoDate: converter
    // para Date deslocaria o dia pelo fuso e marcaria como vencido um calendário que ainda vale.
    const vencido = fim !== '' && fim < toLocalIsoDate(new Date());

    return {
      codCltrprf: dto.codCltrprf,
      numPlbnf: dto.numPlbnf,
      codPrfinv: dto.codPrfinv ?? null,
      dataAlteracao: formatDateIsoToBr(extractIsoDate(dto.datAlterCltrprf)),
      dataInicioVig: formatDateIsoToBr(extractIsoDate(dto.datIniVigSolCltrprf)),
      dataFimVig: formatDateIsoToBr(fim),
      vencido,
    };
  }

  private mapParResponseToModel(dto: PerfilPlanoParticipanteResponseDto): PerfilPlanoParticipante {
    return {
      idPerfilInvest: dto.idPerfilInvest,
      nomePerfil: dto.nomePerfil?.trim() || `Perfil ${dto.idPerfilInvest}`,
      numPlbnf: dto.numPlbnf,
      nomePlano: dto.nomePlano?.trim() || `Plano ${dto.numPlbnf}`,
    };
  }

  private mapAmadeusResponseToModel(dto: PerfilAmadeusResponseDto, numPlbnf: number): PerfilAmadeus {
    const descricao = dto.dcrPrfinv?.trim() || `Código ${dto.codPrfinv}`;
    const codUm = dto.codUm ?? null;
    const nome =
      codUm != null
        ? `${descricao}, Plano ${numPlbnf}, CodUM ${codUm}`
        : `${descricao}, Plano ${numPlbnf} (${dto.codPrfinv})`;

    return { codPrfinv: dto.codPrfinv, numPlbnf, codUm, nome };
  }
}
